import React from 'react';
import { CalendarClock, RotateCcw } from 'lucide-react';

/**
 * TeamMemberDeadlineBar — extracted from RFPAnalysisResult.jsx (Phase 1 refactor).
 * Shown only for Team Members. Displays section deadline and triggers extension request modal.
 * Zero logic changes. All state/handlers passed as props from parent.
 */
const TeamMemberDeadlineBar = ({
    isManager,
    selectedSection,
    editableData,
    toDMY,
    onRequestExtension,
    isNoGo = false,
    isJustApproved = false
}) => {
    if (isManager || isNoGo) return null;
    if (!selectedSection?.deadline && !editableData?.PROPOSAL_DEADLINE) return null;

    const sectionKey = selectedSection?.label || selectedSection?.key || '';

    const statusCode = Number(
        editableData?.SECTION_STATUS_CODE?.[sectionKey] ||
        editableData?.section_status_code?.[sectionKey] ||
        selectedSection?.status_code ||
        selectedSection?.status?.code ||
        0
    );

    const statusLabel = String(
        editableData?.SECTION_STATUS?.[sectionKey] ||
        editableData?.section_status?.[sectionKey] ||
        selectedSection?.status?.label ||
        selectedSection?.status?.name ||
        ''
    ).toLowerCase();

    const isSubmittedOrApproved =
        statusCode === 53 ||
        statusCode === 54 ||
        statusLabel === 'submitted' ||
        statusLabel === 'approved' ||
        statusLabel === 'awaiting review';

    const isAutoClosed =
        statusCode === 56 ||
        statusCode === 57 ||
        statusLabel.includes('auto-closed') ||
        statusLabel.includes('autoclosed') ||
        statusLabel.includes('auto closed');

    if (isSubmittedOrApproved) return null;

    const rawExtStatus = selectedSection?.extension_status;
    const extStatus = (rawExtStatus !== undefined && rawExtStatus !== null)
        ? String(rawExtStatus).toLowerCase().trim()
        : '';
    const isExtPending = extStatus === 'pending' || extStatus === '1' || (selectedSection?.extension_request && !extStatus);
    const isExtApproved = extStatus === 'approved' || extStatus === 'accepted' || extStatus === '2';
    const isExtRejected = extStatus === 'rejected' || extStatus === '3';

    const originalDeadline = selectedSection?.extension_request?.original_deadline_at_request || selectedSection?.extension_request?.original_deadline;
    const requestedDeadline = selectedSection?.extension_request?.employee_requested_deadline || selectedSection?.extension_request?.requested_deadline;

    return (
        <>
            <style>{`
                @keyframes extension-approved-glow {
                    0% {
                        box-shadow: 0 0 0 0px rgba(34, 197, 94, 0.4);
                        transform: scale(1);
                    }
                    30% {
                        box-shadow: 0 0 0 10px rgba(34, 197, 94, 0.2), 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                        transform: scale(1.015);
                        border-color: #22c55e;
                        background: #f0fdf4;
                    }
                    100% {
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                        transform: scale(1);
                    }
                }
                .just-approved-highlight {
                    animation: extension-approved-glow 2.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                    border-color: #22c55e !important;
                    background: #f0fdf4 !important;
                }
            `}</style>
            <div
                className={`relocated-deadline-bar ${isJustApproved ? 'just-approved-highlight' : ''}`}
                style={{
                    margin: '0 2rem 1rem 2rem',
                    padding: '16px 20px',
                    background: isAutoClosed ? '#F1F5F9' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: isAutoClosed ? '1px solid #CBD5E1' : isJustApproved ? '1px solid #22c55e' : '1px solid rgba(226, 232, 240, 0.8)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                    opacity: isAutoClosed ? 0.75 : 1,
                    transition: 'all 0.5s ease-in-out'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: isAutoClosed ? '#E2E8F0' : isJustApproved ? '#DCFCE7' : '#FFF1EC',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.5)',
                        transition: 'background 0.5s ease-in-out'
                    }}>
                        <CalendarClock size={20} color={isAutoClosed ? '#64748B' : isJustApproved ? '#16A34A' : '#FF5526'} strokeWidth={2.5} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ fontSize: '0.65rem', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            {isExtApproved ? 'Extended Deadline' : isExtPending ? 'Current Deadline' : 'Section Deadline'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '800', color: isAutoClosed ? '#64748B' : '#0F172A', lineHeight: '1.2' }}>
                                {(() => {
                                    const rawDate = selectedSection?.deadline || editableData?.PROPOSAL_DEADLINE || editableData?.proposal_deadline;
                                    if (!rawDate || rawDate === 'N/A') return 'No Deadline Set';
                                    return toDMY(rawDate);
                                })()}
                            </div>
                            {isExtApproved && (
                                <div style={{
                                    background: '#DCFCE7',
                                    color: '#15803D',
                                    padding: '4px 10px',
                                    borderRadius: '9999px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    border: '1px solid #BBF7D0',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"></polyline></svg>
                                    <span>Extension Approved</span>
                                </div>
                            )}
                        </div>
                        {isExtApproved && originalDeadline && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#64748B', fontWeight: '500', marginTop: '4px' }}>
                                <span>Extended from {toDMY(originalDeadline)}</span>
                                <span style={{ color: '#22C55E', fontWeight: 'bold' }}>•</span>
                                <span style={{ color: '#16A34A', fontWeight: '600' }}>Approved by Team Lead</span>
                            </div>
                        )}
                        {isExtPending && requestedDeadline && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#64748B', fontWeight: '500', marginTop: '4px' }}>
                                <span>Requested Extension: <strong style={{ color: '#D97706', fontWeight: '700' }}>{toDMY(requestedDeadline)}</strong></span>
                                <span style={{ color: '#D97706', fontWeight: 'bold' }}>•</span>
                                <span style={{ color: '#B45309', fontWeight: '600' }}>Pending Approval</span>
                            </div>
                        )}
                        {isExtRejected && requestedDeadline && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#64748B', fontWeight: '500', marginTop: '4px' }}>
                                <span>Extension requested: {toDMY(requestedDeadline)}</span>
                                <span style={{ color: '#EF4444', fontWeight: 'bold' }}>•</span>
                                <span style={{ color: '#DC2626', fontWeight: '600' }}>Rejected</span>
                            </div>
                        )}
                    </div>
                </div>

                {!isSubmittedOrApproved && !isAutoClosed && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Extension Status Badges */}
                        {isExtPending && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div style={{
                                    background: '#FEF3C7',
                                    color: '#D97706',
                                    padding: '6px 12px',
                                    borderRadius: '999px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    border: '1px solid #FDE68A'
                                }}>
                                    <RotateCcw size={14} className="animate-spin-slow" />
                                    ⏳ Extension Requested
                                </div>
                                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: '500' }}>Waiting for Team Lead approval</span>
                            </div>
                        )}

                        {isExtApproved && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    background: '#F1F5F9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#64748B'
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Last updated</span>
                                    <span style={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: '700' }}>Just now</span>
                                </div>
                            </div>
                        )}

                        {isExtRejected && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div style={{
                                    background: '#FEE2E2',
                                    color: '#DC2626',
                                    padding: '6px 12px',
                                    borderRadius: '999px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    border: '1px solid #FECACA'
                                }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                    ✕ Extension Rejected
                                </div>
                                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: '500' }}>Your extension request was not approved</span>
                            </div>
                        )}

                        {/* Action Button */}
                        {(!isExtPending && !isExtApproved) && (
                            <button
                                className="premium-extension-trigger-btn"
                                onClick={onRequestExtension}
                                style={{
                                    background: 'white',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    padding: '8px 16px',
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: '#FF5526',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = '#FFF1EC';
                                    e.currentTarget.style.borderColor = '#FFD8CB';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.borderColor = '#E2E8F0';
                                }}
                            >
                                <CalendarClock size={14} />
                                Request Extension
                            </button>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default TeamMemberDeadlineBar;
